/**
 * GlobalFilterContext
 * Persistent global filters (Account Manager, Project Manager) applied across all views
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const GlobalFilterContext = createContext()

const STORAGE_KEY = 'cortex_global_filters'

const DEFAULT_FILTERS = { accountManagerId: null, projectManagerId: null, salesCodeId: null }

export const GlobalFilterProvider = ({ children }) => {
  const [filters, setFilters] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? { ...DEFAULT_FILTERS, ...JSON.parse(stored) } : DEFAULT_FILTERS
    } catch {
      return DEFAULT_FILTERS
    }
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  }, [filters])

  const setAccountManager = useCallback((id) => {
    setFilters(prev => ({ ...prev, accountManagerId: id || null }))
  }, [])

  const setProjectManager = useCallback((id) => {
    setFilters(prev => ({ ...prev, projectManagerId: id || null }))
  }, [])

  const setSalesCode = useCallback((id) => {
    setFilters(prev => ({ ...prev, salesCodeId: id || null }))
  }, [])

  const clearAll = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
  }, [])

  const getApiFilters = useCallback(() => ({
    account_manager_id: filters.accountManagerId,
    project_manager_id: filters.projectManagerId,
    sales_code_id: filters.salesCodeId,
  }), [filters.accountManagerId, filters.projectManagerId, filters.salesCodeId])

  const hasActiveFilters = filters.accountManagerId || filters.projectManagerId || filters.salesCodeId

  return (
    <GlobalFilterContext.Provider value={{
      filters,
      setAccountManager,
      setProjectManager,
      setSalesCode,
      clearAll,
      getApiFilters,
      hasActiveFilters,
    }}>
      {children}
    </GlobalFilterContext.Provider>
  )
}

export const useGlobalFilters = () => {
  const context = useContext(GlobalFilterContext)
  if (!context) {
    throw new Error('useGlobalFilters must be used within a GlobalFilterProvider')
  }
  return context
}
